/* Today · Founder Operating System v1 · Batch P2.2 · Feb 2026
 * =============================================================
 * The primary instructor workspace. Replaces the dashboard.
 * The calendar owns the diary view. Lesson preparation and navigation
 * appear only after an explicit Run My Day action.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import DayDiary from "../components/diary/DayDiary";
import CreateBookingSheet from "../components/booking/CreateBookingSheet";
import { Zap } from "lucide-react";
import "./TodayPremium.css";
import { useVisibilityRefetch } from "../hooks/useVisibilityRefetch";
import PupilLessonChoices from "../components/instructor/PupilLessonChoices";
import LearningFollowUpSummary from "../components/instructor/LearningFollowUpSummary";
import Part3RoutePreset from "../components/instructor/Part3RoutePreset";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function Today() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [bookingIntent, setBookingIntent] = useState(null); // {date, time, duration_minutes} — carried in state, never on URL
  const [runDayState, setRunDayState] = useState({ status: "idle", message: "" });
  const [runDayPackage, setRunDayPackage] = useState(null);
  const [runDaySetup, setRunDaySetup] = useState(null);
  const [setupAudit, setSetupAudit] = useState(null);
  const [routeLevels, setRouteLevels] = useState([]);
  const navigate = useNavigate();

  const startRunMyDay = async (options = {}) => {
    const setupOnly = options?.setupOnly === true;
    if (runDayState.status === "checking") return;

    setRunDayState({
      status: "checking",
      message: setupOnly ? "Opening lesson setup…" : "Checking today’s lessons…",
    });
    try {
      let response = await axios.get(`${API}/api/run-my-day/day-package`, {
        withCredentials: true, timeout: 20000,
      });
      let dayPackage = response.data || {};
      setRunDayPackage(dayPackage);

      // Older and newly-created bookings may not yet have a preparation
      // record. Initialise those records here, then ask the server for the
      // authoritative package again. This is idempotent server-side.
      const unprepared = (dayPackage.lessons || []).filter(
        (item) => item.preparation_state === "preparation_not_created"
      );
      if (unprepared.length) {
        await Promise.allSettled(
          unprepared.map((item) =>
            axios.post(
              `${API}/api/run-my-day/lessons/${item.lesson?.id}/prepare`,
              {},
              { withCredentials: true, timeout: 20000 },
            )
          )
        );
        response = await axios.get(`${API}/api/run-my-day/day-package`, {
          withCredentials: true, timeout: 20000,
        });
        dayPackage = response.data || {};
        setRunDayPackage(dayPackage);
      }

      if (!dayPackage.lesson_count) {
        setRunDayState({
          status: "empty",
          message: "Run My Day has no lessons ready to start. Check the bookings in your calendar, then try again.",
        });
        return;
      }

      if (setupOnly) {
        const lessons = dayPackage.lessons || [];
        const blockers = dayPackage.blockers || [];
        const preferred = lessons.find((entry) => entry.lesson?.id === options?.lessonId)
          || lessons.find((entry) => entry.lesson?.id === focus?.id)
          || lessons[0];
        const preferredId = preferred?.lesson?.id;
        const first = blockers.find((entry) => entry.lesson_id === preferredId)
          || blockers[0]
          || {
            lesson_id: preferredId,
            pupil_name: preferred?.pupil_name
              || preferred?.lesson?.pupil_name
              || focus?.title
              || focus?.pupil_name,
          };
        if (!first?.lesson_id) {
          setRunDayState({
            status: "error",
            message: "I couldn’t identify the lesson to set up. Tap the pupil’s car and try again.",
          });
          return;
        }
        const item = lessons.find((entry) => entry.lesson?.id === first.lesson_id) || preferred;
        const geo = item?.operational_geography || {};
        const plan = item?.lesson_plan || item?.approved_plan || {};
        const routeBrief = item?.lesson?.teaching_route_brief || {};
        setRunDaySetup({
          lessonId: first.lesson_id,
          pupilId: item?.pupil_id || null,
          pupilName: first.pupil_name || item?.pupil_name || focus?.title || focus?.pupil_name || "this pupil",
          teachingLevel: Object.keys(plan).length ? plan.desired_level ?? null : item?.lesson?.teaching_level ?? null,
          levelRecommendation: item?.level_recommendation || null,
          learningNextBest: item?.shared_learning_state?.next_best || null,
          objective: plan.desired_focus || item?.lesson?.objective || "",
          concerns: plan.concerns || "",
          intendedOutcome: plan.intended_outcome || "",
          pupilProposal: item?.pupil_proposal || null,
          pupilSubmittedAt: item?.pupil_submitted_at || null,
          pupilResponseLate: Boolean(item?.pupil_response_late),
          pickupLocation: geo.pickup_location || "",
          returnLocation: geo.return_location || "",
          returnToPickup: !geo.return_location || geo.return_location === geo.pickup_location,
          lessonKind: item?.lesson?.lesson_kind || "ordinary",
          testCentreName: routeBrief.test_centre_name || geo.test_centre_name || "",
          testCentreLocation: routeBrief.test_centre_location || geo.test_centre_location || "",
          notifyPupil: false,
          saving: false,
          error: "",
        });
        setRunDayState({
          status: "blocked",
          message: `Complete ${first.pupil_name || focus?.title || "the pupil"}’s lesson plan.`,
        });
        return;
      }

      if (!dayPackage.can_launch_work_mode) {
        const blockers = dayPackage.blockers || [];
        const missing = [...new Set(
          blockers.flatMap((item) => item.missing_information || [])
        )]
          .map((field) => String(field).replaceAll("_", " "))
          .filter(Boolean);
        const detail = missing.length
          ? ` Missing: ${missing.join(", ")}.`
          : "";
        setRunDayState({
          status: "blocked",
          message: `${blockers.length} lesson${blockers.length === 1 ? "" : "s"} need lesson choices or missing details before the day can start.${detail}`,
        });
        const first = blockers[0];
        const item = (dayPackage.lessons || []).find(
          (entry) => entry.lesson?.id === first?.lesson_id
        );
        if (first) {
          const geo = item?.operational_geography || {};
          const plan = item?.lesson_plan || item?.approved_plan || {};
          const routeBrief = item?.lesson?.teaching_route_brief || {};
          setRunDaySetup({
            lessonId: first.lesson_id,
            pupilId: item?.pupil_id || null,
            pupilName: first.pupil_name || "this pupil",
            teachingLevel: Object.keys(plan).length ? plan.desired_level ?? null : item?.lesson?.teaching_level ?? null,
            levelRecommendation: item?.level_recommendation || null,
            learningNextBest: item?.shared_learning_state?.next_best || null,
            objective: plan.desired_focus || item?.lesson?.objective || "",
            concerns: plan.concerns || "",
            intendedOutcome: plan.intended_outcome || "",
            pupilProposal: item?.pupil_proposal || null,
            pupilSubmittedAt: item?.pupil_submitted_at || null,
            pupilResponseLate: Boolean(item?.pupil_response_late),
            pickupLocation: geo.pickup_location || "",
            returnLocation: geo.return_location || "",
            returnToPickup: !geo.return_location || geo.return_location === geo.pickup_location,
            lessonKind: item?.lesson?.lesson_kind || "ordinary",
            testCentreName: routeBrief.test_centre_name || geo.test_centre_name || "",
            testCentreLocation: routeBrief.test_centre_location || geo.test_centre_location || "",
            notifyPupil: false,
            saving: false,
            error: "",
          });
        }
        return;
      }

      sessionStorage.setItem("run_my_day_requested", "1");
      // Carry the exact checked package across the route boundary. Work Mode
      // can still refetch if this payload is unavailable, but it no longer
      // loses the booked pupil or Google leg during navigation.
      sessionStorage.setItem("run_my_day_package", JSON.stringify(dayPackage));
      navigate("/work-mode");
    } catch (error) {
      setRunDayState({
        status: "error",
        message: error.response?.data?.detail?.message || "Run My Day could not check today’s lessons. Please try again.",
      });
    }
  };

  const saveRunDaySetup = async () => {
    if (!runDaySetup || runDaySetup.saving) return;
    setRunDaySetup((s) => ({ ...s, saving: true, error: "" }));
    try {
      const response = await axios.post(
        `${API}/api/run-my-day/lessons/${runDaySetup.lessonId}/setup`,
        {
          teaching_level: runDaySetup.teachingLevel == null ? null : Number(runDaySetup.teachingLevel),
          objective: runDaySetup.objective || null,
          concerns: runDaySetup.concerns || null,
          intended_outcome: runDaySetup.intendedOutcome || null,
          pickup_location: runDaySetup.pickupLocation || null,
          return_location: runDaySetup.returnLocation || null,
          return_to_pickup: runDaySetup.returnToPickup,
          lesson_kind: runDaySetup.lessonKind,
          test_centre_name: runDaySetup.lessonKind === "test_route"
            ? runDaySetup.testCentreName || null
            : null,
          test_centre_location: runDaySetup.lessonKind === "test_route"
            ? runDaySetup.testCentreLocation || null
            : null,
          notify_pupil: Boolean(runDaySetup.notifyPupil),
        },
        { withCredentials: true },
      );
      setSetupAudit({ ...(response.data?.audit || {}), notification: response.data?.notification || null });
      setRunDaySetup(null);
      await startRunMyDay();
    } catch (error) {
      const detail = error.response?.data?.detail;
      setRunDaySetup((s) => ({
        ...s,
        saving: false,
        error: detail?.message || "I still need the missing answer shown below.",
      }));
    }
  };

  const fetchToday = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/today`, { withCredentials: true, timeout: 20000 });
      setData(r.data);
      setState("ok");
    } catch {
      setState("error");
    }
  }, []);

  const fetchRouteLevels = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/run-my-day/route-levels`, { withCredentials: true });
      setRouteLevels(Array.isArray(r.data?.levels) ? r.data.levels : []);
    } catch {
      setRouteLevels([]);
    }
  }, []);

  const refreshToday = useCallback(() => {
    fetchToday();
    fetchRouteLevels();
  }, [fetchToday, fetchRouteLevels]);

  useEffect(() => { refreshToday(); }, [refreshToday]);
  useVisibilityRefetch(refreshToday, { marker: "today" });

  if (state === "loading") {
    return (
      <div data-testid="today-loading" className="min-h-dvh grid place-items-center bg-slate-50 dark:bg-slate-950">
        <p className="text-sm text-slate-500">Loading Today…</p>
      </div>
    );
  }
  if (state !== "ok" || !data) {
    return (
      <div data-testid="today-error" className="min-h-dvh grid place-items-center bg-slate-50 dark:bg-slate-950">
        <div className="space-y-3 text-center p-4" role="alert">
          <p className="text-sm text-rose-600">Couldn&apos;t load your diary.</p>
          <button type="button" data-testid="today-retry" className="min-h-11 rounded-xl bg-blue-700 px-4 text-white font-semibold"
            onClick={() => { setState("loading"); fetchToday(); }}>Retry diary</button>
        </div>
      </div>
    );
  }

  const focus = data.current || data.next;

  return (
    <div data-testid="today" className="min-h-dvh bg-slate-50 dark:bg-slate-950 pb-24">
      <div className="max-w-xl mx-auto p-4 space-y-3">

        {/* Calendar header */}
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black mt-1 text-slate-900 dark:text-white">Today</h1>
          </div>
          <button
            data-testid="today-work-mode-button"
            onClick={startRunMyDay}
            disabled={runDayState.status === "checking"}
            className="px-3 py-2 rounded-lg text-xs font-semibold bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-60 disabled:cursor-wait"
          >
            <Zap className="w-3.5 h-3.5 inline mr-1" />
            {runDayState.status === "checking" ? "Checking…" : "Run My Day"}
          </button>
        </header>

        {/* Part 3 replaces the former GPS/navigation diagnostic panel. */}
        <Part3RoutePreset />

        {runDayState.status !== "idle"
          && runDayState.status !== "checking"
          && (
          <section
            data-testid="run-my-day-status"
            role="status"
            className={`rounded-xl border px-4 py-3 text-sm ${
              runDayState.status === "error"
                ? "border-rose-200 bg-rose-50 text-rose-800"
                : runDayState.status === "blocked"
                  ? "border-amber-200 bg-amber-50 text-amber-900"
                  : "border-sky-200 bg-sky-50 text-sky-900"
            }`}
          >
            <p className="font-semibold">{runDayState.message}</p>
            {runDayState.status === "blocked" && (runDaySetup || runDayPackage?.blockers?.length > 0) && (
              <div className="mt-3 space-y-2" data-testid="run-my-day-blockers">
                {runDaySetup && (
                  <div className="rounded-xl bg-white border border-amber-200 p-3 text-slate-900 space-y-3" data-testid="run-my-day-guided-setup">
                    <div>
                      <p className="font-black">Set up {runDaySetup.pupilName}’s lesson</p>
                      <p className="text-xs text-slate-600">All levels are included automatically unless a specific challenge is chosen.</p>
                    </div>
                    <fieldset>
                      <legend className="text-xs font-bold mb-1">What sort of lesson is this?</legend>
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <button
                          type="button"
                          data-testid="run-my-day-local-training"
                          onClick={() => setRunDaySetup((s) => ({ ...s, lessonKind: "ordinary", error: "" }))}
                          className={`min-h-12 rounded-lg border font-black ${runDaySetup.lessonKind === "ordinary" ? "bg-blue-700 text-white border-blue-700" : "bg-white border-slate-300"}`}
                        >
                          Local training
                        </button>
                        <button
                          type="button"
                          data-testid="run-my-day-test-route"
                          onClick={() => setRunDaySetup((s) => ({ ...s, lessonKind: "test_route", error: "" }))}
                          className={`min-h-12 rounded-lg border font-black ${runDaySetup.lessonKind === "test_route" ? "bg-blue-700 text-white border-blue-700" : "bg-white border-slate-300"}`}
                        >
                          Test-route training
                        </button>
                      </div>
                    </fieldset>
                    <PupilLessonChoices
                      proposal={runDaySetup.pupilProposal}
                      submittedAt={runDaySetup.pupilSubmittedAt}
                      late={runDaySetup.pupilResponseLate}
                      adjusted={Boolean(runDaySetup.pupilProposal && (
                        runDaySetup.objective !== runDaySetup.pupilProposal.desired_focus
                        || runDaySetup.teachingLevel !== (runDaySetup.pupilProposal.desired_level ?? null)
                      ))}
                      onChoose={() => setRunDaySetup((s) => ({
                        ...s,
                        teachingLevel: s.pupilProposal.desired_level ?? null,
                        objective: s.pupilProposal.desired_focus,
                        concerns: s.pupilProposal.concerns,
                        intendedOutcome: s.pupilProposal.intended_outcome,
                        error: "",
                      }))}
                    />
                    <fieldset>
                      <legend className="text-xs font-bold mb-1">Challenge level (adjust if needed)</legend>
                      <button type="button" data-testid="run-my-day-level-auto"
                        aria-pressed={runDaySetup.teachingLevel == null}
                        onClick={() => setRunDaySetup((s) => ({ ...s, teachingLevel: null, error: "" }))}
                        className={`mb-2 w-full min-h-11 rounded-lg border font-bold ${runDaySetup.teachingLevel == null ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-900 border-slate-300"}`}>
                        Automatic — all levels 1–4
                      </button>
                      {runDaySetup.levelRecommendation?.suggested_level && (
                        <div
                          className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-xs text-emerald-950"
                          data-testid="run-my-day-level-recommendation"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="font-black">
                                Suggested today: Level {runDaySetup.levelRecommendation.suggested_level}
                              </p>
                              <p className="mt-1">
                                {(runDaySetup.levelRecommendation.reasons || [])[0]}
                              </p>
                            </div>
                            <button
                              type="button"
                              className="shrink-0 rounded-lg bg-emerald-700 px-3 py-2 font-black text-white"
                              onClick={() => setRunDaySetup((s) => ({
                                ...s,
                                teachingLevel: s.levelRecommendation.suggested_level,
                                error: "",
                              }))}
                            >
                              Choose L{runDaySetup.levelRecommendation.suggested_level}
                            </button>
                          </div>
                          <p className="mt-2 font-semibold">
                            Suggestion only — you choose the level.
                          </p>
                        </div>
                      )}
                      <div className="grid grid-cols-4 gap-2">
                        {[1, 2, 3, 4].map((level) => (
                          <button
                            type="button"
                            key={level}
                            data-testid={`run-my-day-level-${level}`}
                            onClick={() => setRunDaySetup((s) => ({ ...s, teachingLevel: level, error: "" }))}
                            className={`min-h-11 rounded-lg border font-black ${Number(runDaySetup.teachingLevel) === level ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-900 border-slate-300"}`}
                          >
                            L{level}
                          </button>
                        ))}
                      </div>
                      {runDaySetup.learningNextBest && (
                        <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50 px-3 py-3 text-xs text-violet-950" data-testid="run-my-day-next-best">
                          <p className="font-black">Next best move</p>
                          <p className="mt-1">{runDaySetup.learningNextBest.instructor_next_step}</p>
                          <p className="mt-2 font-semibold">Ask: {runDaySetup.learningNextBest.next_question}</p>
                          <p className="mt-2 text-violet-800">Proof: {runDaySetup.learningNextBest.proof_needed}</p>
                        </div>
                      )}
                      {runDaySetup.pupilId && <LearningFollowUpSummary pupilId={runDaySetup.pupilId} />}
                      {runDaySetup.teachingLevel && (
                        <p className="mt-2 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-950" data-testid="run-my-day-level-explanation">
                          {routeLevels.find((item) => item.level === Number(runDaySetup.teachingLevel))?.environment
                            || "Choose the situations the pupil feels ready to try today. Support is agreed separately."}
                        </p>
                      )}
                    </fieldset>
                    {runDaySetup.lessonKind === "test_route" && (
                      <div className="rounded-lg bg-blue-50 px-3 py-3 text-xs text-blue-950 space-y-3">
                        <p>
                          Choose the pupil’s test centre here. I’ll save it to their record, check the Google journey there and back, and protect at least 40 minutes for useful test-area practice.
                        </p>
                        <label className="block font-bold">
                          Test centre
                          <input
                            data-testid="run-my-day-test-centre-name"
                            value={runDaySetup.testCentreName}
                            onChange={(e) => setRunDaySetup((s) => ({ ...s, testCentreName: e.target.value, error: "" }))}
                            placeholder="e.g. Peterborough Test Centre"
                            className="mt-1 w-full min-h-11 rounded-lg border border-blue-200 bg-white px-3 text-base text-slate-900"
                          />
                        </label>
                        <label className="block font-bold">
                          Test centre address or postcode
                          <input
                            data-testid="run-my-day-test-centre-location"
                            value={runDaySetup.testCentreLocation}
                            onChange={(e) => setRunDaySetup((s) => ({ ...s, testCentreLocation: e.target.value, error: "" }))}
                            placeholder="Full address or postcode"
                            className="mt-1 w-full min-h-11 rounded-lg border border-blue-200 bg-white px-3 text-base text-slate-900"
                          />
                        </label>
                      </div>
                    )}
                    <label className="block text-xs font-bold">
                      Lesson goal (adjust if needed) <span className="font-normal text-slate-500">(optional)</span>
                      <input
                        data-testid="run-my-day-objective"
                        value={runDaySetup.objective}
                        onChange={(e) => setRunDaySetup((s) => ({ ...s, objective: e.target.value }))}
                        placeholder="e.g. roundabouts and planning"
                        className="mt-1 w-full min-h-11 rounded-lg border border-slate-300 px-3 text-base"
                      />
                    </label>
                    {(runDaySetup.pupilProposal || runDaySetup.concerns || runDaySetup.intendedOutcome) && <>
                      <label className="block text-xs font-bold">Concerns to consider
                        <textarea value={runDaySetup.concerns} maxLength={1000} rows={2}
                          onChange={(e) => setRunDaySetup((s) => ({ ...s, concerns: e.target.value }))}
                          className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-base" />
                      </label>
                      <label className="block text-xs font-bold">Intended outcome
                        <textarea value={runDaySetup.intendedOutcome} maxLength={500} rows={2}
                          onChange={(e) => setRunDaySetup((s) => ({ ...s, intendedOutcome: e.target.value }))}
                          className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-base" />
                      </label>
                    </>}
                    <label className="block text-xs font-bold">
                      Where should I pick up {runDaySetup.pupilName}?
                      <input
                        data-testid="run-my-day-pickup"
                        value={runDaySetup.pickupLocation}
                        onChange={(e) => setRunDaySetup((s) => ({ ...s, pickupLocation: e.target.value, error: "" }))}
                        placeholder="Saved address or postcode"
                        className="mt-1 w-full min-h-11 rounded-lg border border-slate-300 px-3 text-base"
                      />
                    </label>
                    <label className="flex items-center gap-2 text-sm font-semibold">
                      <input
                        type="checkbox"
                        checked={runDaySetup.returnToPickup}
                        onChange={(e) => setRunDaySetup((s) => ({ ...s, returnToPickup: e.target.checked }))}
                      />
                      Return to the pickup address
                    </label>
                    {!runDaySetup.returnToPickup && (
                      <input
                        aria-label="Return address"
                        value={runDaySetup.returnLocation}
                        onChange={(e) => setRunDaySetup((s) => ({ ...s, returnLocation: e.target.value }))}
                        placeholder="Return address or postcode"
                        className="w-full min-h-11 rounded-lg border border-slate-300 px-3 text-base"
                      />
                    )}
                    <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                      <input
                        type="checkbox"
                        checked={Boolean(runDaySetup.notifyPupil)}
                        onChange={(e) => setRunDaySetup((s) => ({ ...s, notifyPupil: e.target.checked }))}
                        className="mt-0.5"
                      />
                      <span>
                        <strong>Notify {runDaySetup.pupilName} by SMS</strong>
                        <span className="block text-xs font-normal text-slate-600">
                          Optional. Saving the plan and starting Work Mode does not send a message unless you choose this.
                        </span>
                      </span>
                    </label>
                    {runDaySetup.error && <p className="text-sm font-bold text-rose-700" role="alert">{runDaySetup.error}</p>}
                    <button
                      type="button"
                      data-testid="run-my-day-save-setup"
                      onClick={saveRunDaySetup}
                      disabled={runDaySetup.saving}
                      className="w-full min-h-12 rounded-lg bg-blue-700 text-white font-black disabled:opacity-60"
                    >
                      {runDaySetup.saving ? "Saving…" : "Save plan & Run My Day"}
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  data-testid="run-my-day-check-again"
                  onClick={startRunMyDay}
                  className="w-full min-h-11 rounded-lg bg-slate-900 text-white font-bold"
                >
                  Check again
                </button>
              </div>
            )}
          </section>
        )}
        {setupAudit && (
          <p data-testid="run-my-day-audit" className="text-xs text-emerald-300">
            Setup saved and audited. Pupil notice: {setupAudit.notification?.status || "not requested"}.
          </p>
        )}

        {/* ═══ Day Diary — canonical continuous-day timeline.
         *   Same component consumed by InstructorDashboard.
         *   Reads its own data via /api/bookings/slots,
         *   /api/bookings, /api/lesson-events, /api/pupils,
         *   /api/gaps/calendar-gaps. Drag-to-move, tap-to-create,
         *   resize handles, gap highlights, hour lines and
         *   current-time indicator all preserved verbatim.
         *
         *   A quick tap on a booked lesson opens the pupil profile.
         *   Press-and-hold belongs to physical resizing, while a vertical
         *   swipe on the empty timeline belongs to page scrolling. */}
        <section data-testid="today-diary-section">
          <DayDiary
            showHeader={false}
            onOpenLesson={(ev) => {
              const pupilId = ev.pupil_id || ev.pupil_user_id || ev.student_id;
              if (pupilId) {
                window.location.assign(`/pupil/${pupilId}`);
                return;
              }
              // A non-pupil diary item has no profile. Preserve a useful
              // contextual destination instead of constructing a false one.
              if (ev.id) {
                window.location.assign(`/instructor/prep/${ev.id}`);
                return;
              }
              setRunDayState({
                status: "error",
                message: "This diary entry is missing its pupil and lesson links. Refresh the diary; if it remains, the booking needs repairing.",
              });
            }}
            onCreateSlot={(date, time, duration_minutes) =>
              setBookingIntent({ date, time, duration_minutes })}
            onOpenGap={() => navigate("/gap-recovery")}
          />
        </section>

        {/* Booking creation — canonical form, prefilled from the
         *  tapped slot. Intent is carried in React state; the URL
         *  is not used as an intent transport. */}
        <CreateBookingSheet
          open={!!bookingIntent}
          onClose={() => setBookingIntent(null)}
          onCreated={() => {
            setBookingIntent(null);
            // Trigger a diary refetch by remounting via key bump
            // The DayDiary fetches on its own; a soft reload works.
            window.dispatchEvent(new CustomEvent("ndg:diary-refresh"));
          }}
          date={bookingIntent?.date}
          startTime={bookingIntent?.time}
          durationMinutes={bookingIntent?.duration_minutes}
        />
      </div>
      {/* ConversationBar removed · Batch C2a · shell owns the mount. */}
    </div>
  );
}



